import axios from "axios";
import { getAuthToken, logout } from "../features/accounts/auth.service";
import { logApiFailure, logError } from "./logging";
import { NEED_UPGRADE_ERROR } from "./errors";
import { devWarn } from "./util";

export const apiUrl = import.meta.env.VITE_API_URL + "/api/v1";

export type ErrorCodeType = typeof NEED_UPGRADE_ERROR;

export type BaseOperationResult = {
  success: boolean;
  message?: string;
  errors?: string[];
  warnings?: string[];
  errorCode?: ErrorCodeType;
};

export type OperationResult<T> = {
  value?: T;
} & BaseOperationResult;

export type BatchOperationResult<T> = {
  failed: OperationResult<T>[];
  processed: string[];
  inserted: { [key: string]: T };
} & OperationResult<T>;

export const reportApiError = <T>(result: OperationResult<T>) => {
  const h = result.message ?? "";
  let e = "";
  if (Array.isArray(result.errors) && result.errors.length > 0) {
    e = result.errors.join(" ");
  }
  return h + (h && e ? ": " : "") + e;
};

export const reportApiWarnings = <T>(result: OperationResult<T>) => {
  let e = "";
  if (Array.isArray(result.warnings) && result.warnings.length > 0) {
    e = result.warnings.join(" ");
  }
  return e;
};

const instance = axios.create({
  baseURL: apiUrl,
  timeout: 60000, // 60 second default timeout
});

instance.interceptors.request.use(
  async (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    logError(error);
    return Promise.reject(error);
  },
);

// "POST /products/field: <server message>" - the error row's label is capped at 100 chars, and
// "Request failed with status code 500" alone never said which call failed or why.
const describeApiError = (error): string => {
  const method = (error.config?.method ?? "").toUpperCase();
  const path = (error.config?.url ?? "").split("?")[0];
  const data = error.response?.data;
  const serverMessage =
    typeof data?.message === "string"
      ? data.message
      : Array.isArray(data?.errors)
        ? data.errors.join(" ")
        : // A model-binding 400 keys its errors by field ("$.shared"); the keys are the diagnosis
          data?.errors && typeof data.errors === "object"
          ? "invalid " + Object.keys(data.errors).join(", ")
          : "";
  return `${method} ${path}${serverMessage ? ": " + serverMessage : ""}`;
};

instance.interceptors.response.use(
  (response) => response,
  (error) => {
    // The analytics batch reports its own failures; logging them here would queue more events
    const isEventBatch = error.config?.url?.includes("/admin/events");
    // Handle timeout errors specifically
    if (error.code === "ECONNABORTED" && error.message.includes("timeout")) {
      // CM2-1MT etc: Skip Sentry logging for client-side timeouts.
      // Server has its own APM. These are UX indicators, not server errors.
      devWarn(`Request timeout: ${error.config?.url}`);
      if (!isEventBatch) logApiFailure("timeout", describeApiError(error));
    } else if (error.response?.status === 401) {
      logout();
    } else if (error.code === "ERR_NETWORK") {
      // CM2-1RP: Skip logging for network errors (lost WiFi, mobile backgrounding,
      // ad blockers, DNS failures). Not actionable server-side.
      if (!isEventBatch) logApiFailure("network", describeApiError(error));
    } else {
      logError(error, describeApiError(error));
    }
    return Promise.reject(error);
  },
);

export default instance;
